import React, { useState, useEffect } from 'react';
import { Select, Row, Col, Typography } from 'antd';
import AssociatedJobField from './AssociatedJobField';
import { Lineitem } from '../../interfaces/Ordel.Interface';
import { MaterialReservation } from '../../interfaces/MaterialInventory.interface';

interface AttributeCostOption {
	label: string;
	value: string;
}

export interface AttributeCostJobFieldProps {
	/** Available options for the Attribute Cost dropdown */
	options: AttributeCostOption[];
	/** Current attribute cost value */
	attributeCost?: string | null;
	/** Callback when attribute cost changes */
	onAttributeCostChange?: (value: string | null) => void;
	/** When provided (e.g. by Form.Item), used as the attribute cost value instead of attributeCost */
	value?: string | null;
	/** When provided (e.g. by Form.Item), used as the attribute cost change handler instead of onAttributeCostChange */
	onChange?: (value: string | null) => void;
	/** Current associated job */
	associatedJob: Lineitem | null;
	/** Callback when associated job changes */
	onAssociatedJobChange: (job: Lineitem | null) => void;
	/** Whether the fields are disabled */
	disabled?: boolean;
	/** Material reservations to pre-select matching job from */
	materialReservations?: MaterialReservation[];
	/** Layout spans for cost and job columns. Defaults to [12, 12] */
	colSpans?: [number, number];
	/** Style applied to wrapper */
	style?: React.CSSProperties;
	/** Whether the Attribute Cost field is required */
	costRequired?: boolean;
	/** Whether the Associated Job field is required */
	jobRequired?: boolean;
	/** Whether to show an error state for Attribute Cost */
	costError?: boolean;
	/** Whether to show an error state for Associated Job */
	jobError?: boolean;
	/** Inline error message for Attribute Cost (e.g. from form validation) */
	costErrorMessage?: string;
	/** Inline error message for Associated Job (e.g. from form validation) */
	jobErrorMessage?: string;
	/** When provided (e.g. Form.Item wrapping Associated Job), render this instead of the internal job column. Used so parent can wrap job in Form.Item for inline validation. */
	jobColumn?: React.ReactNode;
}

/**
 * Determines if a cost type value is job-related.
 * Supports both snake_case ('fastlane_job_related') and display name ('Fastlane Job Related') formats.
 */
function isJobRelated(value: string | null | undefined): boolean {
	if (!value) return false;
	const lower = value.toLowerCase();
	return lower.includes('job_related') || lower.includes('job related');
}

/**
 * Extracts the production method from a cost type value.
 * Returns 'Swiss' or 'Fastlane' or undefined.
 */
function getProductionMethod(value: string | null | undefined): string | undefined {
	if (!value) return undefined;
	const lower = value.toLowerCase();
	if (lower.includes('swiss')) return 'Swiss';
	if (lower.includes('fastlane')) return 'Fastlane';
	return undefined;
}

/**
 * Finds the matching job-related option for a given production method.
 * Handles both snake_case and display name formats.
 */
function findJobRelatedOption(
	options: AttributeCostOption[],
	method: string
): string | null {
	const methodLower = method.toLowerCase();
	for (const opt of options) {
		const valLower = opt.value.toLowerCase();
		if (
			(valLower.includes('job_related') || valLower.includes('job related')) &&
			valLower.includes(methodLower)
		) {
			return opt.value;
		}
	}
	return null;
}

/**
 * Finds a matching job from material reservations based on production method.
 */
function findMatchingReservationJob(
	reservations: MaterialReservation[] | undefined,
	productionMethod: string | undefined
): Lineitem | null {
	if (!reservations?.length || !productionMethod) return null;
	for (const res of reservations) {
		if (res.order_line_item?.partsbadger_production_method === productionMethod) {
			return res.order_line_item as unknown as Lineitem;
		}
	}
	return null;
}

const AttributeCostJobField: React.FC<AttributeCostJobFieldProps> = ({
	options,
	attributeCost,
	onAttributeCostChange,
	value: valueProp,
	onChange: onChangeProp,
	associatedJob,
	onAssociatedJobChange,
	disabled = false,
	materialReservations,
	colSpans = [12, 12],
	style,
	costRequired = false,
	jobRequired = false,
	costError = false,
	jobError = false,
	costErrorMessage,
	jobErrorMessage,
	jobColumn,
}) => {
	const [autoOpen, setAutoOpen] = useState(false);
	// Support Form.Item: when value/onChange are injected, use them for the attribute cost field
	const costValue = valueProp !== undefined ? valueProp : attributeCost;
	const costOnChange = onChangeProp ?? onAttributeCostChange ?? (() => {});
	const showJob = isJobRelated(costValue);
	const productionMethod = getProductionMethod(costValue);

	// When cost type changes to job-related, try to pre-select from reservations
	useEffect(() => {
		if (showJob && !associatedJob) {
			const match = findMatchingReservationJob(materialReservations, productionMethod);
			if (match) {
				onAssociatedJobChange(match);
				setAutoOpen(false);
			}
		}
	}, [costValue]);

	const handleCostChange = (value: string | null) => {
		costOnChange(value);
		// When used inside Form.Item, onAttributeCostChange can still be passed for side effects (e.g. clear associated job)
		onAttributeCostChange?.(value);
		// Clear previous association
		onAssociatedJobChange(null);

		if (isJobRelated(value)) {
			// Check for matching reservation first
			const method = getProductionMethod(value);
			const match = findMatchingReservationJob(materialReservations, method);
			if (match) {
				onAssociatedJobChange(match);
				setAutoOpen(false);
			} else {
				setAutoOpen(true);
			}
		} else {
			setAutoOpen(false);
		}
	};

	// Match Ant Design Form.Item label (same as Status, Current Length, etc. in parent form)
	const labelStyle: React.CSSProperties = {
		fontSize: 14,
		color: 'rgba(0, 0, 0, 0.88)',
		display: 'block',
		marginBottom: 4,
		fontWeight: 600,
	};

	return (
		<div style={style}>
			<Row gutter={16} style={{ marginBottom: 4 }}>
				<Col span={showJob ? colSpans[0] : 24}>
					<span style={labelStyle}>
						Attribute Cost {costRequired && <Typography.Text type="danger">*</Typography.Text>}
					</span>
				</Col>
				{showJob && (
					<Col span={colSpans[1]}>
						<span style={labelStyle}>
							Associated Job {jobRequired && <Typography.Text type="danger">*</Typography.Text>}
						</span>
					</Col>
				)}
			</Row>
			<Row gutter={16}>
				<Col span={showJob ? colSpans[0] : 24}>
					<Select
						placeholder="Select..."
						options={options}
						value={costValue ?? undefined}
						onChange={(val) => handleCostChange(val ?? null)}
						allowClear
						disabled={disabled}
						style={{ width: '100%' }}
						status={costError ? 'error' : undefined}
					/>
					{costError && (
						<div style={{ color: '#ff4d4f', fontSize: 13, marginTop: 4 }}>
							{costErrorMessage ?? 'Attribute Cost is required'}
						</div>
					)}
				</Col>
				{showJob && (
					<Col span={colSpans[1]}>
						{jobColumn ?? (
							<>
								<AssociatedJobField
									value={associatedJob}
									onChange={(job) => {
										onAssociatedJobChange(job);
										setAutoOpen(false);
										// Sync attribute cost when the selected job's production method
										// differs from the current one (e.g. user picks Swiss job after
										// choosing Fastlane Job Related, or vice versa)
										if (job?.partsbadger_production_method) {
											const currentMethod = getProductionMethod(costValue);
											const jobMethod = job.partsbadger_production_method;
											if (currentMethod && currentMethod !== jobMethod) {
												const match = findJobRelatedOption(options, jobMethod);
												if (match) {
													costOnChange(match);
												}
											}
										}
									}}
									disabled={disabled}
									placeholder="Select Job"
									productionMethodFilter={productionMethod}
									autoOpen={autoOpen}
									status={jobError ? 'error' : undefined}
								/>
								{jobError && (
									<div style={{ color: '#ff4d4f', fontSize: 13, marginTop: 4 }}>
										{jobErrorMessage ?? 'Associated Job is required'}
									</div>
								)}
							</>
						)}
					</Col>
				)}
			</Row>
		</div>
	);
};

export default AttributeCostJobField;
